import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment, createTextVNode as _createTextVNode, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "ml-auto" }
const _hoisted_2 = { class: "flex align-items-center" }
const _hoisted_3 = ["textContent"]
const _hoisted_4 = {
  key: 1,
  class: "text-color-red"
}
const _hoisted_5 = ["textContent"]
const _hoisted_6 = ["textContent"]
const _hoisted_7 = ["textContent"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["id"]
const _hoisted_11 = { class: "font-bold" }
const _hoisted_12 = { class: "flex text-gray-800" }

import { onBeforeMount, onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import AddService from './AddService.vue';
import ServiceGoals from './ServiceGoals.vue';
import { can, useDateTime } from '@/composables';
import {
  TreatmentPlanServiceCreationInterface,
  TreatmentPlanServiceStudentInterface,
  TreatmentPlanServiceUpdateFormInterface,
  TreatmentPlanServiceUpdateInterface,
} from '@/types';
import { PersonStores } from '@/store';
import UpdateService from './UpdateService.vue';
import ConfirmAction from '@/components/ConfirmAction';
import ExpireSwitch from '@/components/ExpireTreatmentServiceSwitch/ExpireSwitch.vue';
import { PERMISSIONS } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'Services',
  setup(__props) {

const loading = ref(true);
const services = ref<TreatmentPlanServiceStudentInterface[]>([]);
const toast = useToast();
const serviceModalOpen = ref(false);
const updateServiceModalOpen = ref(false);
const toggleExpireService = ref(false);
const toggleNoOrganizationModal = ref(false);
const actualService = ref<TreatmentPlanServiceUpdateInterface | number>();
const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const connectionStore = PersonStores.useUserConnectionsStore();
const loadingServiceModal = ref(false);
const expireInformation = ref(0);

onBeforeMount(() => connectionStore.getConnections());

onMounted(() => getServices());

const expandedRows = ref([]);

const getServices: () => Promise<void> = async () => {
  if (can([PERMISSIONS.TREATMENT_PLANS_SERVICES_INDEX])) {
    const filterExpired = treatmentPlansStore.showExpired ? { expired: '1' } : undefined;
    await treatmentPlansStore.getUserTreatmentPlanServices(filterExpired);
    services.value = treatmentPlansStore.treatmentPlanServices;
    loading.value = false;
  }
};

const openServicesModal = () => {
  loadingServiceModal.value = true;
  if (!connectionStore.$state.connections.organizations?.length) {
    toggleNoOrganizationModal.value = true;
  } else {
    serviceModalOpen.value = true;
    loadingServiceModal.value = false;
  }
};

const openUpdateServicesModal = (item: TreatmentPlanServiceUpdateInterface) => {
  actualService.value = item;
  updateServiceModalOpen.value = true;
};

const openExpireServiceModal = async (item: TreatmentPlanServiceUpdateInterface) => {
  actualService.value = item;
  expireInformation.value = (await treatmentPlansStore.getTreatmentPlanExpireInformation(item.id)).future_events;
  toggleExpireService.value = true;
};

const onAddService = async (newService: TreatmentPlanServiceCreationInterface) => {
  const response = await treatmentPlansStore.addNewTreatmentPlanServiceForPerson(newService);

  if (response.status === 201) {
    serviceModalOpen.value = false;
    await onSuccess('Service has been added', 'Saved');
    return;
  }

  onError(response?.data.message);
};

const onCancelAdd = () => (serviceModalOpen.value = false);

const onCancelUpdate = () => (updateServiceModalOpen.value = false);

const onSuccess = async (detail: string, summary: string) => {
  await getServices();
  toast.add({
    severity: 'success',
    summary: summary,
    detail: detail,
    life: 3000,
  });
};

const onError = (detail: string, severity?: string) => {
  toast.add({
    severity: severity ?? 'error',
    summary: 'Error',
    detail: detail,
    life: 3000,
  });
};

const onUpdateService = async (updatedService: TreatmentPlanServiceUpdateFormInterface) => {
  const response = await treatmentPlansStore.updateTreatmentPlanServiceForPerson(
    { ...updatedService },
    updatedService.id,
  );

  if (response.status === 200) {
    updateServiceModalOpen.value = false;
    await onSuccess('Service has been saved', 'Saved');
    return;
  }

  onError(response?.data.message);
};

const onExpireService = async (id: number) => {
  await treatmentPlansStore.expireTreatmentPlanService(id);
  await onSuccess('Service has been expired', 'Expired');
};

const onGoalRemoved = () => onSuccess('Goal has been removed', 'Saved');

const onNewGoal = () => onSuccess('Goal has been saved', 'Saved');

const onGoalUpdate = () => onSuccess('Goal has been updated', 'Saved');

const showExpired = () => {
  treatmentPlansStore.showExpired = true;
  getServices();
};

const hideExpired = () => {
  treatmentPlansStore.showExpired = false;
  getServices();
};

const rowClass = (data: TreatmentPlanServiceStudentInterface) => {
  return {
    'expired-row': data.is_expired,
  };
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_Card, { class: "card-full-width" }, {
    header: _withCtx(() => [
      _cache[4] || (_cache[4] = _createElementVNode("h2", null, "Services", -1)),
      _createVNode(ExpireSwitch, {
        onEnable: showExpired,
        onDisable: hideExpired
      }),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).TREATMENT_PLANS_SERVICES_STORE]))
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              id: "add_service_btn",
              type: "text",
              size: "small",
              text: "Add Service",
              "icon-left": "add",
              loading: loadingServiceModal.value,
              onClick: openServicesModal
            }, null, 8, ["loading"]))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        expandedRows: expandedRows.value,
        "onUpdate:expandedRows": _cache[0] || (_cache[0] = ($event: any) => ((expandedRows).value = $event)),
        value: services.value,
        "data-key": "id",
        "row-hover": true,
        lazy: true,
        scrollable: true,
        "loading-icon": "logo-spinner",
        "responsive-layout": "scroll",
        class: _normalizeClass({ 'table-loading': loading.value }),
        "row-class": rowClass
      }, _createSlots({
        empty: _withCtx(() => [
          _cache[5] || (_cache[5] = _createTextVNode("No Services", -1))
        ]),
        default: _withCtx(() => [
          (services.value.length)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_Column, {
                  expander: _unref(can)([_unref(PERMISSIONS).TREATMENT_SERVICES_GOALS_INDEX]),
                  "header-class": "max-w-4rem",
                  "body-class": "max-w-4rem"
                }, null, 8, ["expander"]),
                _createVNode(_component_Column, {
                  class: "service service-table-item",
                  field: "service",
                  header: "Service",
                  "header-class": "flex justify-content-start align-items-center",
                  "body-class": "px-3 flex align-items-center",
                  style: {"min-width":"270px"}
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("div", {
                      class: _normalizeClass(["flex flex-column gap-1", data.is_expired ? 'expired' : ''])
                    }, [
                      _createElementVNode("div", _hoisted_2, [
                        (data.is_group)
                          ? (_openBlock(), _createBlock(_component_Icon, {
                              key: 0,
                              name: "group",
                              class: "bg-blue text-color-blue-light border-round-xl mr-1",
                              title: "Group",
                              style: {"padding":"2px","font-size":"0.75rem"}
                            }))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", {
                          class: "title w-full",
                          textContent: _toDisplayString(data.service.name)
                        }, null, 8, _hoisted_3),
                        (data.is_expired)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_4, " (Expired)"))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("span", {
                        class: "description px-2 py-1 surface-100 text-sm border-round-sm w-max",
                        textContent: _toDisplayString(data.discipline.name)
                      }, null, 8, _hoisted_5)
                    ], 2)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "duration_description",
                  header: "Duration",
                  class: "service-table-item",
                  "header-class": "flex align-items-center"
                }),
                _createVNode(_component_Column, {
                  field: "start_at",
                  header: "Start Date",
                  class: "service-table-item"
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      textContent: _toDisplayString(_unref(useDateTime)().getLocalDate(data.start_at))
                    }, null, 8, _hoisted_6)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "end_at",
                  header: "End Date",
                  "header-class": "flex align-items-center",
                  class: "service-table-item"
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      class: _normalizeClass({ 'text-color-red': !data.is_active }),
                      textContent: _toDisplayString(_unref(useDateTime)().getLocalDate(data.end_at))
                    }, null, 10, _hoisted_7)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "goals",
                  header: "Goals",
                  "header-class": "flex align-items-center",
                  class: "service-table-item justify-content-center"
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("p", null, _toDisplayString(data.goals.length), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  header: "Expiring",
                  "header-class": "flex align-items-center",
                  class: "service-table-item justify-content-center"
                }, {
                  body: _withCtx(({ data }) => [
                    (data.expiring)
                      ? (_openBlock(), _createBlock(_component_Icon, {
                          key: 0,
                          name: "check",
                          class: "text-lg text-alert-red"
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  field: "actions",
                  header: "Actions",
                  "header-class": "flex align-items-center",
                  class: "service-table-item justify-content-center"
                }, {
                  body: _withCtx(({ data }) => [
                    (_unref(can)([_unref(PERMISSIONS).TREATMENT_PLANS_SERVICES_UPDATE]))
                      ? _withDirectives((_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: "px-2 py-1 cursor-pointer text-color-secondary m-icon edit",
                          onClick: ($event: any) => (openUpdateServicesModal(data))
                        }, null, 8, _hoisted_8)), [
                          [
                            _directive_tooltip,
                            'Edit service',
                            void 0,
                            { bottom: true }
                          ]
                        ])
                      : _createCommentVNode("", true),
                    (_unref(can)([_unref(PERMISSIONS).TREATMENT_PLANS_SERVICE_EXPIRE]))
                      ? _withDirectives((_openBlock(), _createElementBlock("span", {
                          key: 1,
                          class: "px-2 py-1 cursor-pointer text-color-secondary m-icon delete",
                          onClick: ($event: any) => (openExpireServiceModal(data))
                        }, null, 8, _hoisted_9)), [
                          [
                            _directive_tooltip,
                            'Expire service',
                            void 0,
                            { bottom: true }
                          ]
                        ])
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 2
      }, [
        (_unref(can)([_unref(PERMISSIONS).TREATMENT_SERVICES_GOALS_INDEX]))
          ? {
              name: "expansion",
              fn: _withCtx(({ data }) => [
                _createElementVNode("div", {
                  id: `service-${data.id}-goals`,
                  class: "service-goals-container"
                }, [
                  _createVNode(ServiceGoals, {
                    goals: data.goals,
                    "service-id": data.id,
                    "parent-element": `service-${data.id}-goals`,
                    onRemove: onGoalRemoved,
                    onSuccess: onSuccess,
                    onUpdate: onGoalUpdate,
                    onNewGoal: onNewGoal,
                    onError: onError
                  }, null, 8, ["goals", "service-id", "parent-element"])
                ], 8, _hoisted_10)
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["expandedRows", "value", "class"]),
      (serviceModalOpen.value)
        ? (_openBlock(), _createBlock(AddService, {
            key: 0,
            onSave: onAddService,
            onCancel: onCancelAdd
          }))
        : _createCommentVNode("", true),
      (updateServiceModalOpen.value)
        ? (_openBlock(), _createBlock(UpdateService, {
            key: 1,
            service: actualService.value,
            onUpdate: onUpdateService,
            onCancel: onCancelUpdate
          }, null, 8, ["service"]))
        : _createCommentVNode("", true),
      (toggleExpireService.value)
        ? (_openBlock(), _createBlock(_unref(ConfirmAction), {
            key: 2,
            header: "Confirm service expire",
            "accept-label": "Yes",
            "reject-label": "No",
            onAccept: _cache[1] || (_cache[1] = ($event: any) => (onExpireService(actualService.value?.id))),
            onCancel: _cache[2] || (_cache[2] = ($event: any) => (toggleExpireService.value = false))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", null, [
                _cache[9] || (_cache[9] = _createElementVNode("div", { class: "flex items-stretch" }, [
                  _createElementVNode("span", { class: "font-bold" }, [
                    _createElementVNode("i", {
                      class: "pi pi-fill pi-exclamation-triangle",
                      style: {"":"'rgb(var(--primary-500))'"}
                    })
                  ]),
                  _createElementVNode("p", { class: "ml-2" }, "Expiring this service will also:")
                ], -1)),
                _createElementVNode("ol", null, [
                  _cache[8] || (_cache[8] = _createElementVNode("li", null, "Remove the goals associated with the service", -1)),
                  _createElementVNode("li", null, [
                    _cache[6] || (_cache[6] = _createTextVNode(" Remove ", -1)),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(expireInformation.value), 1),
                    _cache[7] || (_cache[7] = _createTextVNode(" future events for this student ", -1))
                  ])
                ])
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (toggleNoOrganizationModal.value)
        ? (_openBlock(), _createBlock(_unref(ConfirmAction), {
            key: 3,
            header: "Student needs to be connected to an Organization",
            "show-footer": false,
            "accept-label": "Ok",
            "reject-label": "Cancel",
            onCancel: _cache[3] || (_cache[3] = ($event: any) => (toggleNoOrganizationModal.value = false))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_12, [
                _createVNode(_component_Icon, {
                  name: "info",
                  style: {}
                }),
                _cache[10] || (_cache[10] = _createElementVNode("span", { class: "w-30rem pl-3" }, "Please connect the student to an organization before creating services.", -1))
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})